import React, { useEffect, useMemo, useState } from "react";
import { apiGetAll, getTimeCodes, getTaskTypes } from "../api";
import { mondayOf, weekStartsBetween, weekDays } from "../utils/weekUtils";
import WeekView from "../components/timeentry/WeekView";
import CalendarMonth from "../components/timeentry/CalendarMonth";
import CsvTools from "../components/timeentry/CsvTools";
import TimerPanel from "../components/timeentry/TimerPanel";

function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function shiftDays(iso, delta) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

// First and last calendar day (ISO) of the month containing `iso`.
function monthBounds(iso) {
  const [y, m] = iso.split("-").map(Number);
  const first = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return [first, last];
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// "June 8 to 14, 2026" / "June 28 to July 4, 2026" / cross-year variant.
function weekRangeLabel(startISO, endISO) {
  const [sy, sm, sd] = startISO.split("-").map(Number);
  const [ey, em, ed] = endISO.split("-").map(Number);
  if (sy === ey && sm === em) return `${MONTHS[sm - 1]} ${sd} to ${ed}, ${sy}`;
  if (sy === ey) return `${MONTHS[sm - 1]} ${sd} to ${MONTHS[em - 1]} ${ed}, ${sy}`;
  return `${MONTHS[sm - 1]} ${sd}, ${sy} to ${MONTHS[em - 1]} ${ed}, ${ey}`;
}

export default function TimeEntryPage({ user }) {
  const [mode, setMode] = useState("week"); // "week" | "month" | "custom"
  const [anchor, setAnchor] = useState(todayISO()); // drives week/month nav
  const [customStart, setCustomStart] = useState(mondayOf(todayISO()));
  const [customEnd, setCustomEnd] = useState(shiftDays(mondayOf(todayISO()), 6));
  const [clients, setClients] = useState([]);
  const [timeCodes, setTimeCodes] = useState([]);
  // Only for the New Task form the grid's create row opens. Fetched once here
  // rather than inside WeekView, which a custom range renders one of per week.
  const [taskTypes, setTaskTypes] = useState([]);

  useEffect(() => {
    (async () => {
      const [cl, tc] = await Promise.all([apiGetAll("/api/clients/"), getTimeCodes()]);
      setClients(Array.isArray(cl) ? cl : cl.results || []);
      setTimeCodes((tc.results || tc).filter((t) => t.is_active));
    })();
    // Separately, and forgivingly: a task type is optional on a task, so
    // failing to load the list must not stop the grid from rendering.
    getTaskTypes()
      .then((tt) => setTaskTypes(Array.isArray(tt) ? tt : tt.results || []))
      .catch(() => setTaskTypes([]));
  }, []);

  // The list of week-start Mondays to render, based on the active period mode.
  const weekStarts = useMemo(() => {
    if (mode === "week") return [mondayOf(anchor)];
    if (mode === "month") {
      const [first, last] = monthBounds(anchor);
      return weekStartsBetween(first, last);
    }
    return weekStartsBetween(customStart, customEnd); // custom
  }, [mode, anchor, customStart, customEnd]);

  const periodLabel = useMemo(() => {
    if (mode === "week") {
      const d = weekDays(mondayOf(anchor));
      return `Week of ${weekRangeLabel(d[0], d[6])}`;
    }
    if (mode === "month") {
      const [y, m] = anchor.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    return weekRangeLabel(customStart, customEnd);
  }, [mode, anchor, customStart, customEnd]);

  const stepPeriod = (dir) => {
    if (mode === "week") setAnchor(shiftDays(anchor, dir * 7));
    else if (mode === "month") {
      const [y, m] = anchor.split("-").map(Number);
      const dt = new Date(y, m - 1 + dir, 1);
      setAnchor(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-01`);
    }
  };

  const modeBtn = (key, label) => (
    <button onClick={() => setMode(key)}
      style={{ padding: "4px 12px", borderRadius: 6, cursor: "pointer",
        border: "1px solid " + (mode === key ? "#2563eb" : "#d1d5db"),
        background: mode === key ? "#eff6ff" : "white",
        color: mode === key ? "#2563eb" : "#374151", fontWeight: 600 }}>
      {label}
    </button>
  );

  // Shared period switcher; `rightExtra` slots in the CSV tools.
  const periodControls = (rightExtra) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
      {modeBtn("week", "Week")}
      {modeBtn("month", "Month")}
      {modeBtn("custom", "Custom")}
      {mode !== "custom" && (
        <>
          <button onClick={() => stepPeriod(-1)}>‹ Prev</button>
          <strong style={{ minWidth: 220, textAlign: "center" }}>{periodLabel}</strong>
          <button onClick={() => stepPeriod(1)}>Next ›</button>
        </>
      )}
      {mode === "custom" && (
        <>
          <label>From <input type="date" value={customStart}
            onChange={(e) => setCustomStart(e.target.value)} /></label>
          <label>To <input type="date" value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)} /></label>
        </>
      )}
      {rightExtra && <span style={{ marginLeft: "auto" }}>{rightExtra}</span>}
    </div>
  );

  // Bumped when the timer books a line, so every grid on the page re-reads.
  const [refreshToken, setRefreshToken] = useState(0);
  const today = todayISO();
  const todayVisible = weekStarts.some((ws) => weekDays(ws).includes(today));

  return (
    // Two columns: the grid, and a narrow rail the timer sticks to. A rail
    // rather than an overlay so a running timer can never sit on top of the
    // CSV tools or the week controls.
    <div style={{ padding: "16px 24px", display: "flex", alignItems: "flex-start", gap: 16 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      {periodControls(
        <CsvTools createdById={user?.id} clients={clients} timeCodes={timeCodes}
          rangeStart={weekStarts[0]}
          rangeEnd={shiftDays(weekStarts[weekStarts.length - 1], 6)} />
      )}
      {mode === "month" ? (
        <CalendarMonth weekStarts={weekStarts} createdById={user?.id}
          clients={clients} timeCodes={timeCodes} />
      ) : (
        weekStarts.map((ws) => (
          <div key={ws} style={{ marginBottom: 20 }}>
            {weekStarts.length > 1 && (
              <div style={{ fontWeight: 700, color: "#374151", margin: "8px 0 4px" }}>
                Week of {weekRangeLabel(ws, shiftDays(ws, 6))}
              </div>
            )}
            <WeekView weekStart={ws} createdById={user?.id}
              clients={clients} timeCodes={timeCodes} taskTypes={taskTypes}
              refreshToken={refreshToken}
              minRows={mode === "week" ? 5 : 1} />
          </div>
        ))
      )}
      </div>
      <div className="te-timer-dock">
        <TimerPanel clients={clients} timeCodes={timeCodes} today={today}
          todayVisible={todayVisible}
          onAdded={() => setRefreshToken((n) => n + 1)} />
      </div>
    </div>
  );
}
